<mode-status-banner
  *ngIf="datasetFieldSettingsFormattingColumnEnabled && hasDefaultFormat"
  status="info"
  show-icon="true"
  accent="blue"
  layout="full-width"
>
  <span
    >Highlighted entries in Example formatting and Default aggregation indicate custom field settings that are local to
    this report.</span
  >
</mode-status-banner>

<div class="table-container">
  <table mat-table [dataSource]="switchViewAttrs">
    <ng-container matColumnDef="name">
      <th
        class="field-name"
        [ngClass]="{ 'shrink-field-width': showFieldDescriptionColumn }"
        data-test="field-name-label"
        mat-header-cell
        *matHeaderCellDef
      >
        Field name
      </th>
      <td
        class="field-name"
        [ngClass]="{ 'shrink-field-width': showFieldDescriptionColumn }"
        data-test="field-name-value"
        mat-cell
        *matCellDef="let attr"
      >
        <span *ngIf="disableCalcFieldsManagement || !attr.isCalc">{{ attr.attrName }}</span>
        <div
          *ngIf="!disableCalcFieldsManagement && attr.isCalc"
          [matMenuTriggerFor]="menu"
          title="Menu"
          #clickHoverMenuTrigger="matMenuTrigger"
        >
          <span>
            {{ attr.attrName }}
            <capra-icon
              *ngIf="hasConflict(attr)"
              capraTooltip
              [tooltipContent]="localNameErrorHTML"
              [tooltipContentIsHtml]="true"
              tooltipPlacement="top"
              class="warning"
              icon="icon-warning"
              data-test="warning-icon"
            ></capra-icon>
          </span>
          <div>
            <capra-icon
              *ngIf="isImportedDataset && attr.isLocal"
              capraTooltip
              tooltipContent="This field is local to the Report"
              tooltipPlacement="top"
              icon="icon-local-field"
              data-test="icon-local-field"
            ></capra-icon>
            <capra-icon icon="icon-caret-down" data-test="dropdown-icon"></capra-icon>
          </div>
        </div>
        <mat-menu class="select-menu" #menu="matMenu" xPosition="before" (closed)="(null)">
          <button (click)="viewCalc(attr)" class="menu__item" mat-menu-item>View formula...</button>
          <button *ngIf="attr.isLocal" (click)="editCalc(attr)" class="menu__item" mat-menu-item>Edit...</button>
          <button (click)="duplicateCalc(attr)" class="menu__item" mat-menu-item>
            {{ attr.isLocal ? 'Duplicate...' : 'Make local copy...' }}
          </button>
          <mat-divider *ngIf="attr.isLocal"></mat-divider>
          <button *ngIf="attr.isLocal" (click)="removeCalc(attr)" class="menu__item" mat-menu-item>Delete</button>
        </mat-menu>
      </td>
    </ng-container>
    <ng-container *ngIf="!showFieldDescriptionColumn" matColumnDef="spacer">
      <th class="spacer" mat-header-cell *matHeaderCellDef></th>
      <td class="spacer" data-test="spacer" mat-cell *matCellDef="let attr"></td>
    </ng-container>
    <ng-container *ngIf="showFieldDescriptionColumn" matColumnDef="description">
      <th class="field-description-header" mat-header-cell *matHeaderCellDef>Field description</th>
      <td class="field-description" data-test="field-description-value" mat-cell *matCellDef="let row">
        <mode-field-description-editor
          [description]="row.attr.desc ?? ''"
          [readOnly]="(row.isLocal !== true && isImportedDataset) || fieldDescReadOnly"
          (editToggled)="fieldDescEditToggled.emit($event)"
          (updateFieldDesc)="updateFieldDesc(row.attr, $event)"
        ></mode-field-description-editor>
      </td>
    </ng-container>
    <ng-container *ngIf="!showFieldDescriptionColumn" matColumnDef="fieldType">
      <th class="field-type" mat-header-cell *matHeaderCellDef>Field type</th>
      <td
        class="field-type"
        [class.error]="!attr.formulaDataType"
        data-test="field-type-value"
        mat-cell
        *matCellDef="let attr"
        data-test="field-type-value"
      >
        <span>{{ (attr.formulaDataType ? attr.role : 'undefined') | titlecase }}</span>
      </td>
    </ng-container>
    <ng-container matColumnDef="dataType">
      <th mat-header-cell *matHeaderCellDef>Data type</th>
      <td [class.error]="!attr.formulaDataType" data-test="data-type-value" mat-cell *matCellDef="let attr">
        <div class="data-type">
          <capra-icon
            capraTooltip
            [tooltipContent]="genericErrorHTML"
            [tooltipContentIsHtml]="true"
            tooltipPlacement="left"
            [tooltipIsDisabled]="attr.formulaDataType"
            [class]="getVarType(attr)"
            [icon]="getFormulaTypeIcon(attr)"
            width="20px"
          ></capra-icon>
          <span>{{ attr.formulaDataType ?? 'undefined' | titlecase }}</span>
        </div>
      </td>
    </ng-container>
    <ng-container *ngIf="datasetFieldSettingsFormattingColumnEnabled" matColumnDef="formatting">
      <th mat-header-cell *matHeaderCellDef>
        Example formatting
        <capra-icon
          capraTooltip
          [tooltipContent]="exampleFormatTooltip"
          [tooltipContentIsHtml]="true"
          [icon]="'icon-info-outline'"
          [class]="'info-icon-formatting'"
          width="20px"
        ></capra-icon>
      </th>
      <td [class.error]="!attr.formulaDataType" data-test="formatting-mat-cell" mat-cell *matCellDef="let attr">
        <div class="formatting" [class.has-formatting]="attr.defaultFormat">
          <ng-container *ngIf="attr?.defaultFormat?.type === 'url'; else formattingTemplate">
            <a
              href="#"
              class="formatting-url"
              [class.has-underline]="attr.defaultFormat.linkStyle === 'underline'"
              [attr.link-color]="attr.defaultFormat.linkColor"
            >
              <ng-container *ngIf="attr.defaultFormat.linkStyle === 'link-icon'; else urlTemplate">
                <capra-icon icon="icon-link"></capra-icon>
              </ng-container>
            </a>
          </ng-container>
          <ng-template #urlTemplate>
            <span>{{ getUrlDisplayLink(attr) }}</span>
          </ng-template>
          <ng-template #formattingTemplate>
            <span>{{ getExampleFormatting(attr) }}</span>
          </ng-template>
        </div>
      </td>
    </ng-container>
    <ng-container matColumnDef="aggregation">
      <th mat-header-cell *matHeaderCellDef>Default aggregation</th>
      <td
        class="aggregation-value"
        [class.error]="!attr.defaultAggregate"
        data-test="aggregation-value"
        mat-cell
        *matCellDef="let attr"
      >
        {{ attr.isNonScalarCalc ? 'Aggregate defined in formula' : (attr.defaultAggregate ?? 'undefined' | titlecase) }}
      </td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
  </table>
  <div *ngIf="switchViewAttrs.length === 0" class="no-filtered-results">
    <capra-icon class="no-results-icon" icon="illo-no-result-table" title="No data to view" data-test="no-data-to-view">
    </capra-icon>
    <h4 class="big-and-bold">No fields match the search</h4>
  </div>
</div>
<footer>
  <div><label>Records</label>{{ totalRecords | number }}</div>
  <div><label>Total fields</label>{{ totalFields }}</div>
  <div *ngIf="!disableCalcFieldsManagement" data-test="footer-calc-fields-count">
    <label>Calculated fields</label>{{ totalCalcFields }}
  </div>
  <div><label>Size</label>{{ size }}</div>
</footer>
